<template>
    <div class="container flex flex-col">
        <h1 class="text-black text-xl lg:text-2xl mt-3 mb-3" v-html="resourceTableName"></h1>
        <div class="mb-3">
            <div class="flex flex-row items-center">
                <div class="w-64 mr-4">
                    <Search v-model="search" placeholder="Search all resources" />
                </div>
                <div class="w-48">
                    <ResourceFilter v-model="filter" data-cy="resourcesFilter" />
                </div>
            </div>
        </div>
        <ResourceList :resources="filteredResources" :endpoint="'resource'" />
    </div>
</template>

<script>
import Search from '@/components/partials/Search'
import ResourceList from '@/components/resource-list/ResourceList'
import ResourceFilter from '@/components/resource-list/partials/ResourceFilter'
import { AuthMixin } from '@/components/shared/mixins/authHelpers'
import camelcaseKeys from 'camelcase-keys'
import GET_RESOURCES from '@/graphql/queries/getResources.gql'
import RoleMixin from '@/components/shared/mixins/roleHelpers'
import { useDrf } from '@/services/featureFlags'
import { api } from '@/services/api'

const FILTER_QUERY_KEY = 'filter'
const FILTER_SURVEYOR = 'surveyor'
const FILTER_ALL = 'all'

export default {
    name: 'ResourcesView',
    components: { Search, ResourceList, ResourceFilter },
    mixins: [ AuthMixin, RoleMixin ],
    data() {
        // Seed the filter from the route query so a refreshed page (or
        // a shared link with ?filter=surveyor) restores the previous
        // selection. Other values fall back to the default — guards
        // against a malformed query bypassing the selection.
        const queryFilter = this.$route && this.$route.query
            ? this.$route.query[FILTER_QUERY_KEY]
            : null
        return {
            search: '',
            resources: [],
            filter: queryFilter === FILTER_SURVEYOR ? FILTER_SURVEYOR : FILTER_ALL,
        }
    },
    apollo: {
        resources: {
            query: GET_RESOURCES,
            debounce: 200,
            variables() {
                return {
                    search: `%${this.search}%`
                }
            },
            skip() {
                return !this.canViewResources || useDrf('getResources')
            },
            update({ resources }) {
                // TODO: need to add code to skip this stuff if user doesn't have
                this.loading = false
                return camelcaseKeys(resources, {deep: true})
            },
            watchLoading (isLoading, countModifier) {
                this.loading = isLoading
            },
            error(error, vm, key, type, options) {
                // for now, we just load it up with random data so we can see something
                this.error = error.message
            }
        },
    },
    mounted() {
        if (useDrf('getResources') && this.canViewResources) {
            this.fetchResourcesDrf()
        }
    },
    methods: {
        async fetchResourcesDrf() {
            this.loading = true
            try {
                this.resources = await api.get('web.resource/', {
                    search: this.search || undefined,
                }) || []
            } catch (e) {
                this.error = e.message
            } finally {
                this.loading = false
            }
        },
    },
    computed: {
        resourceTableName() {
            let title = ''
            if (this.isAdmin) {
                title = 'All'
            } else {
                if (this.isSurveyor) {
                    title = 'Surveyor'
                }
                if (this.hasPracticeManagerRole) {
                    title = title !== '' ? `${title} &amp; Practice ` : 'Practice'
                }
            }
            return `${title} Resources`
        },
        filteredResources() {
            if (!this.resources || !this.resources.length) {
                return []
            }

            return this.resources.filter(resource => {
                // Existing role-based visibility — unchanged.
                let roleVisible = false
                if (this.isAdmin) {
                    roleVisible = true
                } else if (this.isSurveyor && resource.forSurveyor) {
                    roleVisible = true
                } else if (this.hasPracticeManagerRole && resource.forPm) {
                    roleVisible = true
                }
                if (!roleVisible) return false

                // §2.7 user-selected filter, layered on top of the
                // role filter. "All" is the default — passes through
                // whatever the role allows. "Surveyor Only" further
                // restricts to forSurveyor=true rows.
                if (this.filter === FILTER_SURVEYOR) {
                    return resource.forSurveyor === true
                }
                return true
            })
        }
    },
    watch: {
        rolesLoaded(loaded) {
            if (loaded && !this.canViewResources) {
                this.$router.push('/Dashboard')
            }
            if (loaded && this.canViewResources && useDrf('getResources')) {
                this.fetchResourcesDrf()
            }
        },
        search() {
            if (useDrf('getResources')) {
                this.fetchResourcesDrf()
            }
        },
        filter(newValue) {
            // Round-trip the selection through the URL so refresh and
            // link-sharing preserve it. Default value (`all`) drops
            // the param entirely so URLs stay clean when nothing's
            // been narrowed.
            const nextQuery = { ...this.$route.query }
            if (newValue === FILTER_SURVEYOR) {
                nextQuery[FILTER_QUERY_KEY] = FILTER_SURVEYOR
            } else {
                delete nextQuery[FILTER_QUERY_KEY]
            }
            // No-op when the URL already matches (vue-router 3 would
            // otherwise throw NavigationDuplicated).
            if (nextQuery[FILTER_QUERY_KEY] === this.$route.query[FILTER_QUERY_KEY]) {
                return
            }
            const navigation = this.$router.replace({ query: nextQuery })
            // Defensive — vue-router returns a Promise but some test
            // mocks return void; only chain when there's something to
            // chain to.
            if (navigation && typeof navigation.catch === 'function') {
                navigation.catch(() => {})
            }
        },
    }
}
</script>
